/**
 * #417/#530: per-champion, locally-persisted ability slot LAYOUT — the
 * player's drag-and-drop-chosen VISUAL arrangement of the 12 action-bar
 * positions.
 *
 * A `SlotOrder` is a length-`ACTION_SLOT_COUNT` PERMUTATION over LOGICAL
 * indices: `order[visualSlot] === logicalIndex`, i.e. "the ability bar
 * position `visualSlot` currently displays whatever ability sits at position
 * `logicalIndex` of the player's current LOADOUT-filtered ability list" (see
 * `loadout.ts`'s `loadoutCanonicalSlots`) — NOT a content-canonical slot
 * directly. Turning a `logicalIndex` into the real content-canonical slot
 * `abilityLabels.ts`/`abilityFacts.ts`/the wire snapshot's `cast.slot`/
 * `cooldowns[].slot` all key off requires the loadout-filtered composition
 * below — see `LOADOUT_SCHEMA`'s doc comment in `@pit/protocol` for why that
 * slot never gets renumbered.
 *
 * #530 (supersedes an incomplete #513 fix): `SlotOrder`'s values are ONLY
 * ever meaningful through {@link resolveVisualSlotToCanonical} /
 * {@link resolveCanonicalToVisualSlot} below — this was the exact bug three
 * call sites (`ActionBar`, `InputSurface`'s `resolveActionSlot`,
 * `LoadoutPicker`'s `SlotLayoutBar`) each independently re-derived, two of
 * them wrongly (treating a stored value as a raw canonical slot instead of a
 * loadout-filtered logical index), so a keypress could cast a different
 * ability than the one visually displayed at that bar position. No file
 * outside this module may dereference `order[visualSlot]` (or its inverse)
 * directly — every consumer calls the shared resolver instead.
 *
 * This is a PURE client-side display/input-resolution remap:
 *   - `ActionBar` reads it (via the resolver) to decide which canonical
 *     ability's name/icon/cooldown/cast-state to render at each visual
 *     position.
 *   - `InputSurface` reads it (via the resolver) to translate a keybind —
 *     which is bound to a VISUAL position, so pressing "1" always casts
 *     "whatever is in the leftmost slot" — back to the real ability slot the
 *     cast intent must carry.
 *   - `LoadoutPicker`'s `SlotLayoutBar` reads/writes it (via the resolver +
 *     the swap primitives below) so its preview shows exactly what the other
 *     two will resolve for the in-progress (not-yet-submitted) loadout.
 * `packages/sim/src/cast.ts` never sees any of this; it keeps operating on
 * the champion's normal canonical/submitted ability order untouched.
 *
 * DEFAULT / NO-SAVED-LAYOUT: identity (`order[i] === i`), byte-identical to
 * today's canonical order — purely additive/opt-in, per the issue's explicit
 * regression requirement.
 */

import type { AbilityId, ChampionId } from "@pit/protocol";
import { loadAbilityLayout, saveAbilityLayout, type PrefsStorage } from "../prefs.js";
import { ACTION_SLOT_COUNT } from "./abilityLabels.js";
import { loadoutCanonicalSlots } from "./loadout.js";

/** A full-permutation slot order: `order[visualSlot] = logicalIndex` (an
 *  index into the loadout-filtered list — see the file header). */
export type SlotOrder = readonly number[];

/** The canonical/default order — no reorder, byte-identical to today. */
export function identitySlotOrder(count: number = ACTION_SLOT_COUNT): SlotOrder {
  return Array.from({ length: count }, (_, i) => i);
}

/**
 * Is `order` a valid full permutation of `0..count-1`? Guards every stored
 * layout before it's trusted — a wrong length or any duplicate/out-of-range
 * value means "not a real assignment for today's slot count" (e.g. a legacy
 * value from a content update that changed a champion's ability count), and
 * the caller falls back to identity rather than risk two visual slots
 * pointing at the same canonical ability (or one canonical ability vanishing
 * entirely).
 */
export function isValidSlotOrder(order: SlotOrder, count: number = ACTION_SLOT_COUNT): boolean {
  if (order.length !== count) return false;
  const seen = new Set<number>();
  for (const v of order) {
    if (!Number.isInteger(v) || v < 0 || v >= count) return false;
    if (seen.has(v)) return false;
    seen.add(v);
  }
  return true;
}

/**
 * Resolve the EFFECTIVE raw slot order for `championId`: the saved layout if
 * present and valid, else identity. Never throws. An internal building block
 * of {@link resolveVisualSlotToCanonical}/{@link resolveCanonicalToVisualSlot}
 * — a consumer outside this module should call one of those instead of this
 * directly (the raw order's values are logical indices, not canonical slots
 * or anything else meaningful on their own). Still exported for the
 * slot-layout editor's own swap/persist bookkeeping and for tests.
 */
export function resolveSlotOrder(championId: ChampionId, storage?: PrefsStorage): SlotOrder {
  const stored = loadAbilityLayout(championId, storage);
  if (stored === null) return identitySlotOrder();
  return isValidSlotOrder(stored) ? stored : identitySlotOrder();
}

/**
 * Persist `order` as `championId`'s layout. Defensive: only a valid full
 * permutation is ever written — an invalid order (a caller bug, not a user
 * action) is silently dropped rather than corrupting storage with something
 * `resolveSlotOrder` would have to reject anyway.
 */
export function persistSlotOrder(
  championId: ChampionId,
  order: SlotOrder,
  storage?: PrefsStorage,
): void {
  if (!isValidSlotOrder(order)) return;
  saveAbilityLayout(championId, order, storage);
}

/** Swap the values at two VISUAL positions — the drag-and-drop primitive:
 *  dragging the ability currently at `a` onto `b` exchanges the two, so the
 *  result is always a permutation whenever `order` already is one. */
export function swapSlotPositions(order: SlotOrder, a: number, b: number): SlotOrder {
  if (a === b) return order;
  const next = [...order];
  const va = next[a];
  const vb = next[b];
  if (va === undefined || vb === undefined) return order;
  next[a] = vb;
  next[b] = va;
  return next;
}

/**
 * The VISUAL position whose stored permutation value is `value`, or `-1` if
 * `order` doesn't contain it (shouldn't happen for a valid permutation). A
 * generic raw-permutation lookup — see {@link resolveCanonicalToVisualSlot}
 * for the domain-meaningful (logical-index-aware) version every consumer
 * outside this module should actually call.
 */
export function visualSlotFor(order: SlotOrder, value: number): number {
  return order.indexOf(value);
}

/**
 * #530: THE single resolver every consumer (`ActionBar` rendering,
 * `InputSurface`'s keypress-to-cast resolution, and `LoadoutPicker`'s
 * slot-layout editor) calls to translate a VISUAL bar position into the
 * CANONICAL ability slot it currently displays/casts. Composes, in order:
 *   1. the persisted layout permutation ({@link resolveSlotOrder}) —
 *      `visualSlot` -> a LOGICAL index (a position within the player's
 *      current loadout-filtered ability list, NOT a canonical slot);
 *   2. `loadoutCanonicalSlots` (#513) — that logical index -> the real
 *      content-canonical slot.
 *
 * Returns `null` for an EMPTY visual position (fewer selected/pool
 * abilities than `ACTION_SLOT_COUNT`, or a logical index the current
 * loadout doesn't fill) — never a sentinel numeric slot, so a caller can't
 * accidentally forward a "no ability here" result into a real cast.
 */
export function resolveVisualSlotToCanonical(
  championId: ChampionId,
  loadout: readonly AbilityId[] | null,
  visualSlot: number,
  storage?: PrefsStorage,
): number | null {
  const order = resolveSlotOrder(championId, storage);
  const logicalIndex = order[visualSlot] ?? visualSlot;
  const logicalSlots = loadoutCanonicalSlots(championId, loadout);
  const canonical = logicalSlots[logicalIndex];
  return canonical ?? null;
}

/**
 * The inverse of {@link resolveVisualSlotToCanonical}: which VISUAL bar
 * position currently displays `canonicalSlot`, or `null` if it isn't part of
 * the current loadout/layout composition (e.g. it was deselected). Exists so
 * `LoadoutPicker`'s `SlotLayoutBar` (the drag-and-drop editor, which needs to
 * find where a dragged ability's canonical slot CURRENTLY sits before
 * swapping it onto a new visual position) never re-derives the logical-index
 * composition itself — it only ever calls into this module.
 */
export function resolveCanonicalToVisualSlot(
  championId: ChampionId,
  loadout: readonly AbilityId[] | null,
  canonicalSlot: number,
  storage?: PrefsStorage,
): number | null {
  const logicalSlots = loadoutCanonicalSlots(championId, loadout);
  const logicalIndex = logicalSlots.indexOf(canonicalSlot);
  if (logicalIndex === -1) return null;
  const order = resolveSlotOrder(championId, storage);
  const visualSlot = visualSlotFor(order, logicalIndex);
  return visualSlot === -1 ? null : visualSlot;
}
